import { z } from 'zod';
import { centAmount, uuid } from './posting.js';
/**
 * Die Verträge der Zahlungszuordnung — Aufgabe 4 `[ZZ-vorschlag]` des Plans
 * `docs/superpowers/plans/2026-09-05-zahlungszuordnung-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-05-zahlungszuordnung-design.md`
 * (Abschnitt 3 und 4).
 *
 * **Was hier steht: die Form, in der eine Zuordnung über die Leitung geht.**
 * Vier Dinge und nicht mehr — der Vorschlag samt seinen fünf Befunden, die
 * Zuordnung, ihre Position und die drei Aufzählungen, die beide brauchen. Die
 * Anfrageformen der sieben Endpunkte aus Spec 5.1 entstehen mit ihnen
 * (`ZZ-dienst`, Task 5); sie hier vorwegzunehmen hiesse, einen Vertrag zu
 * schreiben, den kein Endpunkt liest — und `pnpm api:spec` nähme ihn mangels
 * Aufrufer ohnehin nicht in `docs/openapi/openapi.json` auf.
 *
 * **Kein Geldbetrag als `number`, auch nicht hier** (Hausregel 1). `centAmount`
 * gibt ein `Money` zurück, nie eine Zeichenkette.
 *
 * **Die vier Aufzählungen stehen hier und nicht in `apps/api`.** Sie gehen über
 * die Schnittstelle — als Feld der Antwort und später als Feld der Anfrage —,
 * und eine zweite Deklaration derselben Werte in `apps/api/src` wäre genau der
 * Fund, den `pnpm api:enums:check` meldet. Die reine Vorschlagsfunktion
 * (`apps/api/src/zahlungen/vorschlag.ts`) importiert die Typen von hier, statt
 * sie nachzuschreiben.
 *
 * **Die Werte sind deckungsgleich mit den `CHECK`-Bedingungen der Migration
 * `0160`** (Aufgabe `ZZ-schema`, parallel gebaut). Diese Datei legt keine
 * Migration an und behauptet keine; sie nennt die Werte, die Spec 3.1 als
 * Katalog beider Spalten führt.
 */
/**
 * Woher das Geld kommt, das hier zugeordnet wird (`D-28`, Spec 3.1).
 *
 * `geldbewegung` — eine Bank- oder Zahldienstbewegung. `guthaben` — der
 * Handweg, auf dem ein Sachbearbeiter auf Kundenwunsch ein Kundenguthaben
 * gegen eine offene Rechnung verrechnet (`ZZ-guthaben`, Task 9).
 *
 * **Genau eine der beiden Quellen je Kopf**, nie beide und nie keine: Bei
 * `geldbewegung` steht die Bewegung, bei `guthaben` der Kunde. Die Bedingung
 * hält die Datenbank (`E-877`), nicht dieser Vertrag — hier steht der Katalog.
 */
export const zahlungZuordnungQuelleSchema = z.enum(['geldbewegung', 'guthaben']);
/**
 * Wie eine Zuordnung entstanden ist — die Messgrösse dieser Ära, nach dem
 * Vorbild von `kontierungHerkunftSchema` (Spec 3.1).
 *
 * `automatisch` ist der **reine Volltreffer** und der einzige Wert, der ohne
 * Vier-Augen bucht (`D-14`, Ask-Runde `ZO-1`): genau eine Rechnungsnummer,
 * Beleg offen, Bruttobetrag gleich offenem Rest, keine Aufteilung. Sobald eine
 * Position `mahnkosten` oder `verzugszinsen` trägt oder ein Rest bleibt, ist es
 * kein Volltreffer mehr, und die Freigabe greift wieder (`E-876`).
 *
 * `vorschlag_angenommen` / `vorschlag_geaendert` — ein Mensch hat den Vorschlag
 * dieser Ära übernommen oder abgewandelt. `von_hand` — er hat ohne Vorschlag
 * zugeordnet. `guthaben_verrechnung` — der Handweg aus `D-28`. `korrektur` und
 * `aufgehoben` behaupten das Gegenteil einer früheren Zuordnung und tragen
 * deshalb einen Vorgänger und eine Begründung (`D-23`).
 */
export const zahlungZuordnungHerkunftSchema = z.enum([
    'automatisch',
    'vorschlag_angenommen',
    'vorschlag_geaendert',
    'von_hand',
    'guthaben_verrechnung',
    'korrektur',
    'aufgehoben',
]);
/**
 * Was eine Position tilgt (`D-4`, Spec 3.1).
 *
 * **Drei Arten und kein Konto.** Welches Sachkonto daraus wird — `1400`,
 * `2700`, `2650` —, entscheidet der Buchungsweg (`ZZ-buchung`, Task 6) und
 * nicht dieser Vertrag: Kein Verhalten hängt an der Zahl, nur an der Art.
 *
 * **Mahnkosten und Verzugszinsen erscheinen ausschliesslich, wenn sie GEZAHLT
 * worden sind** (`D-4`). Diese Ära bucht einen gezahlten Betrag, sie rechnet
 * keinen aus, und sie nimmt weder Kosten noch Zinsen je in die Forderung auf.
 */
export const zahlungZuordnungArtSchema = z.enum([
    'hauptforderung',
    'mahnkosten',
    'verzugszinsen',
]);
/**
 * Die fünf Befunde des Vorschlags (Spec 4).
 *
 * | Befund | Wann | Folge |
 * |---|---|---|
 * | `volltreffer` | genau eine plausible Nummer, Beleg offen, Bruttobetrag gleich offenem Rest | eine Position `hauptforderung`, gebucht ohne Freigabe (`D-14`) |
 * | `teilzahlung` | Bruttobetrag kleiner als der offene Rest | eine Position über den Bruttobetrag, Beleg bleibt „Offen" (`D-5`) |
 * | `ueberzahlung` | Bruttobetrag grösser als der offene Rest | **kein** Vorschlag, Klärung (`D-11`) |
 * | `mehrdeutig` | mehr als eine plausible Nummer im Text | **kein** Vorschlag, Klärung (`D-9`) |
 * | `kein_treffer` | keine plausible Nummer, Beleg unbekannt, storniert oder ausgeglichen | **kein** Vorschlag, Klärung (`D-1`, `D-12`) |
 *
 * **Eine Ausnahme von „eine Position", benannt** (Nachtrag vom 2026-09-25,
 * `LS-einzugerkennung`): Die Sammelgutschrift einer eigenen Lastschriftdatei
 * mit mehreren Posten trägt als `volltreffer` bzw. `teilzahlung` je Einzug der
 * Datei eine Position `hauptforderung` (`apps/api/src/zahlungen/sammeleinzug.ts`).
 *
 * **`mehrdeutig` nimmt nie die erste Nummer** (`D-9`). Dieselbe Haltung, mit
 * der `zahldienste/erkennung.ts::erkenne()` bei mehreren Positionstexten
 * `unplausibel` meldet, statt eine davon auszuwählen: Eine stille Wahl wäre in
 * der Buchung ein Teilbetrag, den niemand als solchen erkennt.
 */
export const zuordnungsbefundSchema = z.enum([
    'volltreffer',
    'teilzahlung',
    'ueberzahlung',
    'mehrdeutig',
    'kein_treffer',
]);
/**
 * Eine Position einer bestehenden Zuordnung — je Beleg und Art ein Betrag
 * (Spec 3.1).
 *
 * **`amount` ist vorzeichenbehaftet und nicht auf positiv beschränkt**, nach
 * dem Vorbild von `kontierungPositionInputSchema`: Die Datenbank verlangt nur,
 * dass der Betrag nicht null ist und die Summe aller Positionen zwischen null
 * und dem Bruttobetrag der Bewegung liegt (Bedingung 6 aus Spec 3.2, `E-853`).
 * Diese Grenze prüft die Datenbank beim `COMMIT` und nicht dieses Schema — ein
 * Vertrag, der sie nachschriebe, wäre eine zweite Wahrheit über dieselbe Summe.
 *
 * **`customerId` und `kundennummer` sind abgeleitet und nur lesend** (seit
 * 2026-09-30, `ZZ-mkweb`, Mehrkunden-Spec
 * `docs/superpowers/specs/2026-09-26-mehrkunden-sammeleinzug-design.md`
 * `D-1`, `D-20`, Abschnitt 8): der Kunde des Belegs (`document.customer_id`)
 * und seine Anzeigenummer (`customer.display_customer_no`). Die Position
 * trägt keine eigene Kundenspalte (`D-1`) — der Kunde steht unveränderlich am
 * Beleg —, und die Eingabeform {@link zahlungZuordnungPositionInputSchema}
 * nimmt ausdrücklich keinen Kunden an. `null` nur an einem Beleg ohne Kunden.
 */
export const zahlungZuordnungPositionSchema = z
    .object({
    id: uuid,
    position: z.number().int().positive(),
    documentId: uuid,
    documentNumber: z.string(),
    customerId: uuid.nullable(),
    kundennummer: z.string().nullable(),
    art: zahlungZuordnungArtSchema,
    amount: centAmount,
    begruendung: z.string().nullable(),
})
    .strict();
/**
 * Eine Zuordnung, wie sie gelesen wird — Kopf plus 1..n Positionen (`D-7`).
 *
 * **Die Kette ist anfügend und nicht änderbar.** Eine Korrektur oder eine
 * Aufhebung legt eine NEUE Zeile an, die über `ersetztZuordnungId` auf ihren
 * Vorgänger zeigt; die alte Zeile bleibt unverändert stehen (`E-852`). Genau
 * eine ungeersetzte Zuordnung je Geldbewegung (`E-848`); ein Kunde darf
 * dagegen mehrere Guthabenverrechnungen tragen, weil jede eine eigene
 * Erklärung ist.
 *
 * **`kontierungId` ist die einzige Brücke ins Journal** (`D-13`). Die Freigabe
 * legt eine `kontierung` an und ruft den vorhandenen Buchungspfad; diese Ära
 * schreibt keine `journal_line` selbst. Solange das Feld `null` ist, ist die
 * Zuordnung fachlich getroffen und buchhalterisch noch nicht vollzogen — genau
 * das führt die Kontrollsicht `zahlung_zuordnung_ohne_buchung` (`E-857`).
 *
 * **Eine `aufgehoben`-Zeile trägt KEINE Position, jede andere mindestens eine**
 * (`E-1736`, seit 2026-10-06, `ZZ-aufhebungsform`). Die Aufhebung behauptet
 * das Gegenteil einer Tilgung und hat nichts aufzuteilen — dieselbe Ausnahme 1,
 * mit der `assert_zahlung_zuordnung_positionen()` (`0160`) die Mindestzahl für
 * genau diese Herkunft aussetzt. Bis zu diesem Tag stand hier pauschal
 * `positionen.min(1)`: Die Antwort von `POST …/:id/aufheben` und jede Kette
 * mit einer aufgehobenen Zeile konnten den Vertrag strukturell nie erfüllen,
 * und der Browser meldete eine „unerwartete Form", nachdem die Aufhebung
 * serverseitig längst ausgeführt war. Andere Herkünfte als `aufgehoben` kennt
 * `zahlung_zuordnung` nicht ohne Position — `storniert` ist ein Wert von
 * `kontierung.herkunft` (`0082`) und keiner dieser Tabelle.
 *
 * **Warum `superRefine` und keine diskriminierte Vereinigung.** Die
 * Schnittstellenbeschreibung liest bei `ZodEffects` die Eingabeseite
 * (`apps/api/src/openapi/json-schema.ts`): Die Komponente `ZahlungZuordnung`
 * bleibt EIN Objekt mit denselben Feldern, nur ohne `minItems: 1` — eine
 * Lockerung, die kein bestehender Leser bemerkt. Eine Vereinigung am Feld
 * `herkunft` erschiene dort als `oneOf` zweier Zweige statt als ein Objekt,
 * nähme der Komponente ihre `properties` (Regel 5 der Bruchprüfung,
 * `apps/api/src/openapi/bruch.ts`) und spaltete den Typ `ZahlungZuordnung`,
 * den Kette, Freigabe und Anlage gemeinsam lesen, in zwei Formen, die sich
 * allein in der Länge einer Liste unterscheiden. Die Regel ist eine
 * Querbedingung zweier Felder EINER Zeile — dieselbe Art Regel, die die
 * Datenbank in ihrer aufgeschobenen Prüfung hält —, und dafür ist
 * `superRefine` die Form. Sie lässt `aufgehoben` leere Positionen tragen
 * (`DARF`) und verlangt nicht, dass sie leer SIND: Das hält die Datenbank
 * nicht, und ein Vertrag, der strenger wäre als der Bestand, wäre die nächste
 * „unerwartete Form".
 */
export const zahlungZuordnungSchema = z
    .object({
    id: uuid,
    quelle: zahlungZuordnungQuelleSchema,
    bankTransactionId: uuid.nullable(),
    customerId: uuid.nullable(),
    herkunft: zahlungZuordnungHerkunftSchema,
    begruendung: z.string().nullable(),
    ersetztZuordnungId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /**
     * Einzelfreigabe (`D-31`, `0263`): Wer die Zuordnung angelegt hat, hat sie
     * auch freigegeben — nur unter einer Freigaberegel ohne vier Augen.
     * `optional()`, weil ältere Fixturen das Feld nicht führen.
     */
    einzelfreigabe: z.boolean().optional(),
    kontierungId: uuid.nullable(),
    createdAt: z.string(),
    positionen: z.array(zahlungZuordnungPositionSchema),
})
    .strict()
    .superRefine((zuordnung, ctx) => {
    if (zuordnung.herkunft !== 'aufgehoben' && zuordnung.positionen.length < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: 1,
            inclusive: true,
            type: 'array',
            path: ['positionen'],
            message: `Eine Zuordnung mit Herkunft "${zuordnung.herkunft}" trägt mindestens eine Position; nur "aufgehoben" trägt keine.`,
        });
    }
});
/**
 * Eine vorgeschlagene Position — Beleg, Art, Betrag, sonst nichts.
 *
 * **Der Vorschlag schlägt ausschliesslich `hauptforderung` vor**, und das ist
 * eine Entscheidung und keine Lücke: Ob ein Teil der Zahlung Mahnkosten oder
 * Verzugszinsen tilgt, ist eine Rechtsanwendung (`D-4`), und diese Ära rechnet
 * keinen Zinsbetrag aus. Die Aufteilung trifft ein Mensch — und genau dadurch
 * ist sie kein Volltreffer mehr und behält die Freigabe (`E-876`).
 *
 * **`customerId` und `kundennummer` wie an {@link zahlungZuordnungPositionSchema}**
 * — abgeleitet aus dem Beleg, nur lesend (`ZZ-mkweb`, `D-20`). Wer einen
 * Mehrkunden-Vorschlag annimmt, sieht damit, wessen Rechnung welchen Betrag
 * bekommt; die Anlage schickt den Kunden nicht zurück.
 */
export const zahlungsvorschlagPositionSchema = z
    .object({
    documentId: uuid,
    documentNumber: z.string(),
    customerId: uuid.nullable(),
    kundennummer: z.string().nullable(),
    art: zahlungZuordnungArtSchema,
    amount: centAmount,
})
    .strict();
/**
 * Die **Sammeleinzugsangabe** eines Vorschlags (`ZZ-mkweb`, Mehrkunden-Spec
 * Abschnitt 8, Punkt 2): die eigene Lastschriftdatei, der eine
 * Sammelgutschrift zugeordnet wird — ihre MsgId, die Zahl ihrer Einzüge und
 * die Zahl der verschiedenen Kunden darunter.
 *
 * **Sie kommt aus der getroffenen Datei** (`apps/api/src/zahlungen/sammeleinzug.ts`,
 * `Sammelentscheid.datei`) und steht nur dort, wo genau eine Datei getroffen
 * hat. Ein Vorschlag über eine Rechnungsnummer im Verwendungszweck trägt
 * `null`, ebenso eine Sammelgutschrift, zu der keine oder mehr als eine Datei
 * passt — dann gibt es keine Datei, die man nennen könnte.
 *
 * `anzahl` ist `lastschrift_datei.anzahl` (der eingefrorene Dateikopf, `0231`),
 * `kundenzahl` die Zahl der verschiedenen Kunden über die verknüpften Einzüge.
 */
export const zahlungsvorschlagSammeleinzugSchema = z
    .object({
    msgId: z.string(),
    anzahl: z.number().int().positive(),
    kundenzahl: z.number().int().nonnegative(),
})
    .strict();
/**
 * Der Vorschlag zu einer Geldbewegung (Spec 4).
 *
 * **`bruttobetrag` ist Betrag zuzüglich Gebühr und nie der Nettozufluss**
 * (`D-10`). Ohne diese Zurechnung wären 52 von 55 gemessenen PayPal-Zahlungen
 * Teilzahlungen — die Gebühr trägt das Unternehmen, und die Rechnung ist voll
 * getilgt.
 *
 * **`positionen` ist bei drei der fünf Befunde leer.** `ueberzahlung`,
 * `mehrdeutig` und `kein_treffer` erzeugen ausdrücklich keinen Vorschlag; sie
 * tragen stattdessen einen `grund`, der in der Klärliste steht.
 *
 * **`nummern` führt jede gefundene plausible Rechnungsnummer**, auch die
 * verworfenen. Bei `mehrdeutig` sind es die, zwischen denen ein Mensch
 * entscheidet; bei `kein_treffer` ist die Liste leer oder nennt die eine
 * Nummer, die keinen Beleg getroffen hat — damit im Klärfall sichtbar ist,
 * **wonach** gesucht wurde, und nicht nur, dass nichts gefunden wurde.
 * Dieselbe Unterscheidung wie zwischen `unplausibel` und `kein_treffer` in
 * `zahldienste/erkennung.ts` (`E-231`).
 *
 * **`sammeleinzug` ist bei jedem Vorschlag vorhanden** — die Angabe
 * {@link zahlungsvorschlagSammeleinzugSchema} oder `null` (`ZZ-mkweb`,
 * 2026-09-30). Ein Feld, das mal fehlt und mal `null` ist, wären zwei
 * Schreibweisen desselben „nichts".
 */
export const zahlungsvorschlagSchema = z
    .object({
    befund: zuordnungsbefundSchema,
    bruttobetrag: centAmount,
    nummern: z.array(z.string()),
    positionen: z.array(zahlungsvorschlagPositionSchema),
    grund: z.string().nullable(),
    sammeleinzug: zahlungsvorschlagSammeleinzugSchema.nullable(),
})
    .strict();
// ===========================================================================
// DIE ANFRAGEFORMEN DES DIENSTES — Aufgabe 5 `[ZZ-dienst]`
// ===========================================================================
//
// **Sie stehen hier und nicht in `apps/api/src/zahlungen/`**, aus demselben
// Grund wie die vier Aufzählungen oben: Sie gehen über die Schnittstelle,
// `pnpm api:spec` liest sie aus dem Vertrag, und eine zweite Deklaration
// derselben Felder in `apps/api/src` wäre der Fund, den `pnpm api:enums:check`
// meldet.
//
// **Die Herkunft ist beim Anlegen auf DREI Werte verengt** — die drei, die ein
// Mensch wählt. `automatisch` vergibt allein der Dienst (`D-14`, der reine
// Volltreffer), `guthaben_verrechnung` allein der Handweg (`D-28`,
// `ZZ-guthaben`), `korrektur` und `aufgehoben` allein die Folgewege. Wäre
// `automatisch` hier wählbar, hätte jeder Benutzer einen Weg, die
// Vier-Augen-Ausnahme aus `zahlung_zuordnung_vier_augen` (`0160`) selbst zu
// ziehen — dieselbe Begründung, mit der `KontierungService.anlegen()` die
// maschinellen Herkünfte abweist und mit der `ZAHLUNGSZUORDNUNG_HERKUNFT`
// (`apps/api/src/zahlungen/herkunft.ts`) dort abgewiesen wird.
/**
 * Die drei Herkünfte, die ein Mensch beim Anlegen wählen darf.
 *
 * Kein eigener Katalog neben `zahlungZuordnungHerkunftSchema`, sondern seine
 * Verengung: `z.enum` über eine Teilmenge derselben Zeichenketten, und der
 * Typ bleibt zuweisbar an `ZahlungZuordnungHerkunft`.
 */
export const zahlungZuordnungHandherkunftSchema = z.enum([
    'vorschlag_angenommen',
    'vorschlag_geaendert',
    'von_hand',
]);
/**
 * Eine Position, wie sie hereinkommt — Beleg, Art, Betrag, wahlweise ein Grund.
 *
 * **Ohne `position`**: Die Reihenfolge ist die des Feldes, und der Dienst
 * nummeriert ab 1 durch. Dieselbe Wahl wie bei
 * `kontierungPositionInputSchema`; eine mitgeschickte Nummer wäre ein zweiter
 * Ort für dieselbe Ordnung und die erste Stelle, an der eine Lücke entstünde.
 */
export const zahlungZuordnungPositionInputSchema = z
    .object({
    documentId: uuid,
    art: zahlungZuordnungArtSchema,
    amount: centAmount,
    begruendung: z.string().trim().min(1).max(2000).optional(),
})
    .strict();
/**
 * `POST /api/v1/zahlungen/zuordnungen` — anlegen.
 *
 * **`ersetztZuordnungId` ist die WIEDERZUORDNUNG nach einer Aufhebung** und
 * nicht die Korrektur einer gültigen Zuordnung. Migration `0160` lässt je
 * Bewegung genau eine Zeile ohne Vorgänger zu
 * (`zahlung_zuordnung_bewegung_wurzel`); wer eine aufgehobene Bewegung neu
 * zuordnet, setzt die Kette fort, statt eine zweite Wurzel anzulegen. Ohne das
 * Feld wäre eine einmal aufgehobene Bewegung für immer unzuordenbar.
 *
 * **`begruendung` ist hier freiwillig und in der Datenbank Pflicht, sobald die
 * Herkunft `von_hand` lautet** (`zahlung_zuordnung_begruendung_pflicht`,
 * `D-23`). Der Vertrag schreibt die Bedingung nicht nach: Sie hängt am Wert
 * eines anderen Feldes, und zwei Fassungen derselben Regel — eine im Vertrag,
 * eine im `CHECK` — laufen genau dann auseinander, wenn eine von beiden
 * geändert wird. Der Dienst weist sie mit einem eigenen Code ab, bevor die
 * Datenbank sie mit `23514` abwiese.
 */
export const zahlungZuordnungCreateRequestSchema = z
    .object({
    bankTransactionId: uuid,
    herkunft: zahlungZuordnungHandherkunftSchema,
    begruendung: z.string().trim().min(1).max(2000).optional(),
    ersetztZuordnungId: uuid.optional(),
    positionen: z.array(zahlungZuordnungPositionInputSchema).min(1),
})
    .strict();
/**
 * `POST /api/v1/zahlungen/zuordnungen/:id/aufheben` — die Aufhebung.
 *
 * Nur die Begründung, und sie ist **Pflicht** (`D-23`,
 * `zahlung_zuordnung_begruendung_pflicht`): Eine Aufhebung behauptet das
 * Gegenteil einer früheren Zuordnung, und wer das tut, muss sagen, warum.
 */
export const zahlungZuordnungAufhebungRequestSchema = z
    .object({ begruendung: z.string().trim().min(1).max(2000) })
    .strict();
/**
 * Der Zahlungsstand eines Belegs — die **Ableitung** aus `D-5`/`D-15`.
 *
 * **Kein vierter `document.status`.** Der Zahlungsstand wird gerechnet:
 * Bruttobetrag des Belegs minus Summe der freigegebenen, ungeersetzten
 * Zuordnungen auf seine Hauptforderung. Eine Spalte „Restbetrag" liefe gegen
 * die Zuordnungen auseinander, sobald eine Zuordnung aufgehoben wird.
 *
 * **`giltAlsErledigt` ist nicht `rest === '0.00'`** (`D-29`, Ask-Runde
 * `ZO-4`): Ein Rest bis einschliesslich 0,05 € gilt als erledigt. Die
 * Toleranz gilt **nur nach unten** — eine Überzahlung um Cent ergibt einen
 * negativen Rest und bleibt Klärfall (`D-11`).
 */
export const zahlungsstandSchema = z
    .object({
    documentId: uuid,
    documentNumber: z.string().nullable(),
    bruttobetrag: centAmount,
    zugeordnet: centAmount,
    rest: centAmount,
    giltAlsErledigt: z.boolean(),
})
    .strict();
// ===========================================================================
// DIE KLÄRUNGSLISTE — Aufgabe 7 `[ZZ-web]`
// ===========================================================================
//
// **Der Vertrag der Sicht `zahlung_klaerung`** (Migration `0161`, Aufgabe 3
// `[ZZ-sichten]`), gelesen von `GET /api/v1/zahlungen/klaerung` und
// dargestellt von `apps/web/src/zahlungen/KlaerungPage.tsx`.
//
// **Er steht hier und nicht in `apps/api/src/zahlungen/`**, aus demselben
// Grund wie die Aufzählungen oben: Die Klärgründe gehen über die
// Schnittstelle, `pnpm api:spec` liest sie aus dem Vertrag, und eine zweite
// Deklaration derselben Werte in `apps/api/src` wäre der Fund, den
// `pnpm api:enums:check` meldet.
/**
 * Warum ein Geldeingang wartet — **SECHS** Werte und nicht die fünf aus
 * Spec 7.
 *
 * | Grund | Was er sagt |
 * |---|---|
 * | `kein_treffer` | Kein Ziffernlauf des Verwendungszwecks ist die Nummer eines Forderungsbelegs |
 * | `mehrdeutig` | Mehr als einer ist es — und die erste zu nehmen ist ausdrücklich verboten (`D-9`) |
 * | `ueberzahlung` | Der Bruttobetrag übersteigt den offenen Rest des getroffenen Belegs (`D-11`) |
 * | `beleg_storniert` | Der getroffene Beleg ist storniert und tilgt nichts |
 * | `beleg_ausgeglichen` | Der getroffene Beleg ist bereits erledigt — auch der, den das Altsystem als bezahlt führt |
 * | `zuordenbar` | Nichts steht entgegen; die Zuordnung ist nur noch nicht getroffen |
 *
 * **`zuordenbar` ist der sechste und der wichtigste.** Spec 7 nennt fünf
 * Gründe, und alle fünf sagen, warum eine Bewegung **nicht** zugeordnet
 * werden kann. Ein Eingang, der zugeordnet werden **könnte** und es nur noch
 * nicht ist, fiele durch alle fünf — und genau das ist beim Cutover der
 * gesamte Rückstand. Die Sicht `zahlung_klaerung` führt ihn deshalb unter
 * eigenem Namen (Migration `0161`, datierter Nachtrag in Task 3 des Plans);
 * eine Liste, die ihn wegliesse, machte ihre eigene Vollständigkeitszusage
 * falsch (`E-854`).
 *
 * **Die Reihenfolge ist total und überschneidungsfrei.** Jede Bewegung trägt
 * GENAU EINEN Grund — die Sicht entscheidet ihn in einer `CASE`-Kette, nicht
 * in einem `UNION ALL`. Die Oberfläche darf deshalb nach Grund gruppieren,
 * ohne eine Bewegung doppelt zu zeigen.
 */
export const zahlungKlaerungsgrundSchema = z.enum([
    'kein_treffer',
    'mehrdeutig',
    'ueberzahlung',
    'beleg_storniert',
    'beleg_ausgeglichen',
    'zuordenbar',
]);
/**
 * Eine Zeile der Klärungsliste — eine Geldbewegung ohne geltende Zuordnung.
 *
 * **`brutto` ist Betrag zuzüglich Gebühr** und damit derselbe Wert, gegen den
 * der Vorschlag den offenen Rest hält (`D-10`). `amount` und `feeAmount`
 * stehen daneben, weil ein Mensch, der eine Aufteilung entscheidet, den
 * Nettozufluss auf seinem Kontoauszug wiederfinden muss.
 *
 * **`documentId`, `documentNumber` und `offenerRest` sind genau dann gesetzt,
 * wenn `treffer === 1`.** Bei `kein_treffer` gibt es keinen Beleg, bei
 * `mehrdeutig` mehr als einen — und die Sicht nennt dann keinen, statt einen
 * auszuwählen. Die Oberfläche liest daran ab, ob sie einen Beleg vorbelegen
 * darf.
 *
 * **`treffer` zählt die Belege, deren Nummer im Verwendungszweck steht**, und
 * ist damit die Zahl, an der `kein_treffer` und `mehrdeutig` auseinandergehen.
 * Sie steht ausdrücklich in der Antwort und wird nicht aus dem Grund
 * zurückgerechnet: „gesucht und nichts getroffen" ist etwas anderes als
 * „nichts gesucht", und nur die Zahl kann das sagen.
 */
export const zahlungKlaerungszeileSchema = z
    .object({
    grund: zahlungKlaerungsgrundSchema,
    bankTransactionId: uuid,
    bankAccountId: uuid,
    provider: z.string(),
    bookingDate: z.string(),
    valueDate: z.string(),
    amount: centAmount,
    feeAmount: centAmount.nullable(),
    brutto: centAmount,
    currency: z.string(),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    matchStatus: z.string(),
    matchKind: z.string().nullable(),
    treffer: z.number().int().nonnegative(),
    documentId: uuid.nullable(),
    documentNumber: z.string().nullable(),
    offenerRest: centAmount.nullable(),
})
    .strict();
/**
 * `GET /api/v1/zahlungen/klaerung` — die Antwortform.
 *
 * **`gesamt` ist die Zahl aller Klärfälle und nicht die Länge von `zeilen`.**
 * Die Liste ist begrenzt (`limit`), die Zahl nicht: Ein Arbeitsvorrat, dessen
 * Grösse man nur an der Länge einer abgeschnittenen Seite ablesen kann, sagt
 * nach dem Cutover „200", solange mehr als 200 warten. Dieselbe Bauform wie
 * `UnkontiertQuery` (`total`/`gesamtOhneFilter`).
 */
export const zahlungKlaerungslisteSchema = z
    .object({
    zeilen: z.array(zahlungKlaerungszeileSchema),
    gesamt: z.number().int().nonnegative(),
})
    .strict();
// ===========================================================================
// DER GUTHABEN-HANDWEG — Aufgabe 9 `[ZZ-guthaben]`
// ===========================================================================
//
// **Er steht hier und nicht in `apps/api/src/zahlungen/`**, aus demselben
// Grund wie die Aufzählungen oben: Die Form geht über die Schnittstelle,
// `pnpm api:spec` liest sie aus dem Vertrag, und eine zweite Deklaration
// derselben Felder in `apps/api/src` wäre der Fund, den
// `pnpm api:enums:check` meldet.
/**
 * `POST /api/v1/zahlungen/guthabenverrechnung` — der **Handweg** aus `D-28`
 * (Ask-Runde `ZO-2`, 2026-09-05): Ein Sachbearbeiter verrechnet auf
 * Kundenwunsch ein Kundenguthaben gegen eine offene Rechnung.
 *
 * **Drei Felder und kein viertes.**
 *
 * **Kein `customerId`.** Der Kunde steht am Beleg (`document.customer_id`)
 * und wird von dort gelesen. Ein zweites Feld dafür wäre die erste Stelle,
 * an der ein Aufrufer das Guthaben eines FREMDEN Kunden gegen diese Rechnung
 * buchen könnte — und die Einlösungssperre (`1_000_008`, je Kunde) schützte
 * dann den falschen Kunden.
 *
 * **`begruendung` ist PFLICHT und nicht wahlfrei** — anders als bei
 * `zahlungZuordnungCreateRequestSchema`, wo sie erst die Herkunft `von_hand`
 * verlangt. `D-28` sagt „mit Begründung und Freigabe", die Datenbank hält
 * dieselbe Pflicht (`zahlung_zuordnung_begruendung_pflicht` nimmt
 * `guthaben_verrechnung` mit auf, `0160`), und hier ist sie eine Aussage
 * über den WILLEN DES KUNDEN, die niemand sonst rekonstruieren kann: Eine
 * Aufrechnung ohne festgehaltenen Anlass ist genau der Fall, gegen den
 * `D-2` entschieden hat.
 *
 * **Kein `idempotencyKey`.** Er trüge hier nichts: Diese Anfrage bucht nicht
 * — sie legt eine Zuordnung an, die auf ihre Freigabe wartet (`D-14`), und
 * erst die Freigabe ruft `GuthabenEinloesungService.einloesen()`. Zwei
 * versehentlich abgeschickte Anfragen ergeben zwei Zuordnungen, die BEIDE
 * einzeln freigegeben werden müssten; die zweite Freigabe läuft dann gegen
 * `pruefeEinloesbar()` und dessen Prüfung „bereits eingelöst" auf. Der
 * Riegel sitzt also an der Stelle, an der Geld bewegt wird, und nicht an
 * der, an der eine Absicht festgehalten wird.
 *
 * **Kein `art`-Feld.** Eine Guthabenverrechnung tilgt ausschliesslich die
 * **Hauptforderung**: Der vorhandene Buchungsweg bucht `1796` an `1400` und
 * kennt weder `2700` (Mahnkosten) noch `2650` (Verzugszinsen). Wer Mahnkosten
 * aus Guthaben verrechnen wollte, brauchte einen zweiten Buchungssatz — und
 * genau den verbietet `D-28`.
 */
export const guthabenVerrechnungRequestSchema = z
    .object({
    documentId: uuid,
    betrag: centAmount,
    begruendung: z.string().trim().min(1).max(2000),
})
    .strict();
// ===========================================================================
// DIE FREIGABELISTE UND DIE KETTE EINER BEWEGUNG — Auftrag `ZZ-freigabeliste`
// ===========================================================================
//
// **Warum es beide gibt, und warum sie erst jetzt entstehen.** Aufgabe 5
// `[ZZ-dienst]` hat `POST /zahlungen/zuordnungen/:id/freigeben` gebaut und
// Aufgabe 9 `[ZZ-guthaben]` den Handweg; beide sagen im Kopfkommentar ihres
// Controllers ausdrücklich, dass sie keine Oberfläche mitbringen. Damit blieb
// eine Lücke, die kein Wächter meldet und die erst beim Benutzen auffällt:
//
// 1. **Wer `zahlung:freigeben` trägt, fand die wartenden Zuordnungen
//    nirgends.** `zahlung_klaerung` (`0161`) führt sie ausdrücklich NICHT —
//    eine Bewegung mit angelegter, ungeersetzter Zuordnung ist dort kein
//    Klärfall mehr, obwohl sie fachlich noch nichts getilgt hat. Ohne eine
//    eigene Liste wäre die Vier-Augen-Regel aus `D-14` eine Zusicherung ohne
//    Weg.
// 2. **Nach einer Aufhebung war die Bewegung unzuordenbar.** `0160` lässt je
//    Bewegung genau eine Zeile ohne Vorgänger zu
//    (`zahlung_zuordnung_bewegung_wurzel`); die Wiederzuordnung MUSS deshalb
//    `ersetztZuordnungId` mitschicken, und die Kennung der aufgehobenen Zeile
//    stand nirgends, wo die Oberfläche sie hätte lesen können. Der Dialog
//    lief in `409 ZAHLUNG_ZUORDNUNG_ALREADY_EXISTS`.
//
// **Zwei Formen und nicht eine.** Die Freigabeliste ist eine ARBEITSLISTE
// über viele Bewegungen und trägt deshalb neben der Zuordnung auch das,
// woran ein Mensch sie wiedererkennt (Verwendungszweck, Buchungstag, Kanal,
// Kunde). Die Kette ist die GESCHICHTE genau einer Bewegung und besteht aus
// unveränderten `ZahlungZuordnung`-Zeilen — dieselbe Form, die
// `POST /zahlungen/zuordnungen` zurückgibt. Sie hier nachzuschreiben wäre
// eine zweite Wahrheit über dieselbe Zeile.
/**
 * Eine Zuordnung, die auf ihr zweites Augenpaar wartet.
 *
 * **`createdBy` steht hier und nicht in `zahlungZuordnungSchema`.** Die
 * Oberfläche braucht die Kennung des Urhebers für genau eine Auskunft: Die
 * eigene Zuordnung lässt sich nicht selbst freigeben (`E-851`,
 * `ZAHLUNG_ZUORDNUNG_APPROVAL_SELF`), und ein Knopf, den der Server ohnehin
 * abweist, gehört ausgegraut und beschriftet statt anklickbar. Dieselbe
 * Wahl wie bei `GuthabenErstattung.createdBy` und derselbe Grundsatz: Die
 * Markierung ist eine **Auskunft, kein Schutz** — der Riegel steht in der
 * Datenbank.
 *
 * **Die Bewegungsfelder sind bei `quelle = 'guthaben'` `null`**, die
 * Kundenfelder bei `quelle = 'geldbewegung'`. Genau eine der beiden Quellen
 * je Kopf ist eine Datenbankbedingung (`E-877`); dieser Vertrag schreibt sie
 * nicht nach, er lässt beide Seiten offen und überlässt das Urteil der
 * Zeile.
 *
 * **`brutto` ist der Bruttobetrag der Bewegung** — Betrag zuzüglich Gebühr
 * (`D-10`) —, `summe` die Summe der Positionen. Sie stehen NEBENEINANDER und
 * nicht statt einander: Der Unterschied zwischen beiden ist der Rest, den die
 * Zuordnung offen lässt, und genau das ist die Zahl, an der ein Freigeber
 * eine Teilzahlung erkennt.
 */
export const zahlungZuordnungFreigabeZeileSchema = z
    .object({
    id: uuid,
    quelle: zahlungZuordnungQuelleSchema,
    herkunft: zahlungZuordnungHerkunftSchema,
    begruendung: z.string().nullable(),
    ersetztZuordnungId: uuid.nullable(),
    createdBy: uuid,
    createdAt: z.string(),
    summe: centAmount,
    bankTransactionId: uuid.nullable(),
    bookingDate: z.string().nullable(),
    provider: z.string().nullable(),
    purpose: z.string().nullable(),
    brutto: centAmount.nullable(),
    customerId: uuid.nullable(),
    kundeName: z.string().nullable(),
    positionen: z.array(zahlungZuordnungPositionSchema).min(1),
})
    .strict();
/**
 * `GET /api/v1/zahlungen/zuordnungen/wartend` — die Antwortform.
 *
 * **`gesamt` zählt an `limit` vorbei**, dieselbe Bauform wie
 * `zahlungKlaerungslisteSchema` und aus demselben Grund: Die Grösse eines
 * Arbeitsvorrats an der Länge einer abgeschnittenen Seite abzulesen ist genau
 * die Auskunft, die man nicht bekommt.
 */
export const zahlungZuordnungFreigabelisteSchema = z
    .object({
    zeilen: z.array(zahlungZuordnungFreigabeZeileSchema),
    gesamt: z.number().int().nonnegative(),
})
    .strict();
/**
 * Warum die Kette eine Fortsetzung nennt oder nicht — `ZZ-fortsetzungkette`,
 * `E-1720`.
 *
 * - `ersetzbar`: die gültige Zeile darf ersetzt werden (aufgehoben, oder die
 *   Bewegung trägt keine Buchung); `fortsetzung` nennt sie.
 * - `gebucht`: die gültige Zeile ist gebucht und nicht aufgehoben; eine
 *   Ersetzung wiese der Anlageweg mit `409 ZAHLUNG_ZUORDNUNG_GEBUCHT_NICHT_ERSETZBAR`
 *   ab. `fortsetzung` ist `null`; der Weg ist: erst aufheben, dann neu zuordnen.
 * - `keine`: die Bewegung trägt noch keine Zuordnung; die neue wird die Wurzel.
 */
export const zahlungZuordnungFortsetzungGrundSchema = z.enum(['ersetzbar', 'gebucht', 'keine']);
/**
 * `GET /api/v1/zahlungen/zuordnungen/bewegung/:bankTransactionId` — die
 * vollständige Kette einer Bewegung, älteste Zeile zuerst.
 *
 * **`fortsetzung` ist die eigentliche Auskunft.** Sie nennt die Kennung der
 * Zeile, an die eine NEUE Zuordnung anschliessen muss — die ungeersetzte
 * Zeile am Ende der Kette —, oder `null`, wenn die Bewegung noch keine
 * Zuordnung trägt und die neue die Wurzel wird. Sie wird hier GERECHNET und
 * nicht von der Oberfläche: Wer sie im Browser aus `ersetztZuordnungId`
 * zurückrechnete, hätte eine zweite Fassung derselben Kettenregel, und die
 * liefe beim nächsten Schärfen von `zahlung_zuordnung_bewegung_wurzel`
 * auseinander.
 *
 * **Sie ist NICHT die Auskunft „darf zugeordnet werden".** Trägt die Kette
 * eine geltende, nicht aufgehobene, ungebuchte Zuordnung, ist `fortsetzung`
 * deren Kennung — und der Anlageweg weist die Anfrage trotzdem mit
 * `409 ZAHLUNG_ZUORDNUNG_ALREADY_EXISTS` ab, wenn `ersetztZuordnungId` fehlt.
 * Das Urteil trifft der Dienst (`pruefeWurzelFrei()`), nicht diese Zahl.
 *
 * **Aber sie nennt nur, was der Dienst auch annimmt** (`E-1720`): Eine
 * gebuchte, nicht aufgehobene Zeile ist keine Fortsetzung (`null`), und
 * `fortsetzungGrund` sagt `gebucht`, damit die Oberfläche den Satz „zuerst
 * aufheben, dann neu zuordnen" zeigen kann, statt eine 409 zu provozieren.
 */
export const zahlungZuordnungKetteSchema = z
    .object({
    bankTransactionId: uuid,
    fortsetzung: uuid.nullable(),
    fortsetzungGrund: zahlungZuordnungFortsetzungGrundSchema,
    zuordnungen: z.array(zahlungZuordnungSchema),
})
    .strict();
//# sourceMappingURL=zahlungszuordnung.js.map